import { useCallback, useEffect, useMemo } from "react";
import { useDebounce } from "./useDebounce";
import { useSearchParams } from "./useSearchParams";

type Type = "regex" | "in" | "nin" | "eq" | "ne";

export type FilterRule = {
  id: string;
  value: string;
  type?: Type;
};

type ReturnType = [FilterRule[], (rules: FilterRule[]) => void, string];

export const useTableFilters = (
  allowedKeys: Record<string, string>,
  idToFilter?: (id: string) => string,
  filterToId?: (filter: string) => string,
): ReturnType => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(
    () =>
      Array.from(searchParams)
        .map(([id, value]) => {
          return filterToId ? { id: filterToId(id), value } : { id, value };
        })
        .filter(({ id }) => Object.values(allowedKeys).includes(id as any))
        .map(({ id, value }) => {
          return {
            id,
            value: value.split(":")[1],
            type: value.split(":")[0] as Type,
          };
        }),
    [allowedKeys, searchParams],
  );

  const updateFilters = useCallback(
    (rules: FilterRule[]) => {
      Object.values(allowedKeys).forEach((key) => {
        const k = idToFilter ? idToFilter(key) : key;
        searchParams.delete(k);
      });
      rules.forEach((rule) => {
        const formattedRuleName = idToFilter ? idToFilter(rule.id) : rule.id;
        searchParams.set(
          formattedRuleName,
          `${rule.type || "regex"}:${rule.value}`,
        );
      });

      setSearchParams(searchParams);
    },
    [allowedKeys, searchParams, setSearchParams],
  );

  const parsedFilters = useMemo(()=> 
   filters
    .map((x) => `${idToFilter ? idToFilter(x.id) : x.id}=${x.value}`)
    .join("&")
  , [filters])

  useEffect(() => {
    updateFilters(filters);
  }, [filters, updateFilters]);

  const debouncedUpdateFilters = useDebounce(updateFilters);

  return [filters, debouncedUpdateFilters, parsedFilters];
};
